import type { JSX, ReactNode } from 'react';
import React from 'react';

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';

import './ToggleButton.css';


export function MarkerActiveIndicator(): JSX.Element {
  return <span data-testid="toggle-radio-indicator" />;
}

interface ToggleButtonProps {
  label: ReactNode;
  isActive?: boolean;
  onClick?: (event: React.MouseEvent<HTMLLabelElement>) => void;
  subLabel?: ReactNode;
}

interface SproutRadioIconProps {
  checked: boolean;
}

/* ----------------------------------
   Component
---------------------------------- */

function ToggleButton({
  label,
  isActive = false,
  onClick,
  subLabel
}: ToggleButtonProps): JSX.Element {
  // Custom Sprout radio icon
  function SproutRadioIcon({
    checked
  }: SproutRadioIconProps): JSX.Element {
    return (
      <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 24 }}>
        <circle
          cx="12"
          cy="12"
          r="9"
          fill="none"
          stroke={
            checked
              ? 'var(--brand-leaf-green)'
              : 'var(--neutral-400)'
          }
          strokeWidth="2"
        />
        {checked && (
          <circle
            cx="12"
            cy="12"
            r="5"
            fill="var(--brand-leaf-green)"
          />
        )}
      </SvgIcon>
    );
  }

  return (
    <Box
      component="label"
      onClick={e => {
        onClick && onClick(e);
      }}
      className="toggleButtonRoot"
      sx={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '16px'
      }}
    >
      <Radio
        checked={Boolean(isActive)}
        tabIndex={-1}
        value={label}
        icon={<SproutRadioIcon checked={false} />}
        checkedIcon={<SproutRadioIcon checked={true} />}
        disableRipple
        sx={{
          p: 0.5,
          '&:hover': {
            backgroundColor: 'rgba(0, 125, 52, 0.08)'
          },
          '&.Mui-disabled': {
            color: 'var(--neutral-300)'
          }
        }}
      />

      {/* Preserve old test signal: only render this when active */}
      {isActive && <MarkerActiveIndicator />}

      <Box className="toggleButtonText" sx={{ ml: 1 }}>
        <Typography component="span">{label}</Typography>
        {subLabel && (
          <FormHelperText className="toggleButtonHelper">
            {subLabel}
          </FormHelperText>
        )}
      </Box>
    </Box>
  );
}

export default ToggleButton;